import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Slider,
  TextField,
  Typography,
} from '@mui/material';

interface Question {
  key: string;
  text: string;
  type: string;
  order: number;
  options?: (string | null)[] | null;
}

interface SurveyFormProps {
  questions: Question[];
  onSubmit: (answers: Record<string, unknown>) => void;
  loading: boolean;
}

const likertMarks = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

const SurveyForm: React.FC<SurveyFormProps> = ({ questions, onSubmit, loading }) => {
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    questions.forEach(q => {
      if (q.type === 'likert_5') {
        initial[q.key] = 3;
      } else if (q.type === 'multi_select') {
        initial[q.key] = { selected: [] as string[], comment: '' };
      } else if (q.type === 'free_text') {
        initial[q.key] = '';
      }
    });
    return initial;
  });
  const [submitted, setSubmitted] = useState(false);

  const handleLikertChange = (key: string, value: number | number[]) => {
    setAnswers(prev => ({ ...prev, [key]: value as number }));
  };

  const handleMultiSelectToggle = (key: string, option: string) => {
    setAnswers(prev => {
      const current = prev[key] as { selected: string[]; comment: string };
      const selected = current.selected.includes(option)
        ? current.selected.filter(o => o !== option)
        : [...current.selected, option];
      return { ...prev, [key]: { ...current, selected } };
    });
  };

  const handleMultiSelectComment = (key: string, comment: string) => {
    setAnswers(prev => {
      const current = prev[key] as { selected: string[]; comment: string };
      return { ...prev, [key]: { ...current, comment } };
    });
  };

  const handleFreeTextChange = (key: string, value: string) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit(answers);
    setSubmitted(true);
  };

  if (submitted) {
    return <Alert severity="success">Survey response submitted successfully.</Alert>;
  }

  const sorted = [...questions].sort((a, b) => a.order - b.order);

  return (
    <Box>
      {sorted.map(question => (
        <Box key={question.key} mb={4}>
          <Typography variant="subtitle1" fontWeight="medium" mb={1}>
            {question.text}
          </Typography>

          {question.type === 'likert_5' && (
            <Box px={2}>
              <Slider
                value={(answers[question.key] as number) ?? 3}
                onChange={(_, val) => handleLikertChange(question.key, val)}
                min={1}
                max={5}
                step={1}
                marks={likertMarks}
                valueLabelDisplay="auto"
              />
            </Box>
          )}

          {question.type === 'multi_select' && (
            <Box>
              <FormGroup>
                {(question.options || []).map(option => {
                  if (!option) return null;
                  const current = answers[question.key] as { selected: string[]; comment: string };
                  return (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={current.selected.includes(option)}
                          onChange={() => handleMultiSelectToggle(question.key, option)}
                        />
                      }
                      label={option}
                    />
                  );
                })}
              </FormGroup>
              <TextField
                label="Additional comments"
                multiline
                rows={2}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                value={(answers[question.key] as { selected: string[]; comment: string }).comment}
                onChange={e => handleMultiSelectComment(question.key, e.target.value)}
              />
            </Box>
          )}

          {question.type === 'free_text' && (
            <TextField
              multiline
              rows={3}
              fullWidth
              value={answers[question.key] as string}
              onChange={e => handleFreeTextChange(question.key, e.target.value)}
            />
          )}
        </Box>
      ))}

      <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ mt: 2 }}>
        {loading ? <CircularProgress size={20} /> : 'Submit Survey'}
      </Button>
    </Box>
  );
};

export default SurveyForm;
